import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../../utils/axiosInstance";

const HEADERS = [
  "Code",
  "Project Name",
  "Client",
  "Start Date",
  "Deadline",
  "Status",
  "Primary Manager",
  "Total to receive, INR",
  "Total to pay, INR",
  "Profit Margin, %",
];

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await api.get("/projects", { withCredentials: true });
        setProjects(response.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch projects");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getClientName = (c) => {
    if (!c) return "—";
    if (c.type?.toLowerCase() === "company") return c.company_name || "—";
    if (c.primary_user)
      return `${c.primary_user.first_name} ${c.primary_user.last_name}`.trim();
    return "—";
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate("/projects/create-project")}
            className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
          >
            Add Project
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-blue-600" />
        </div>
      )}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{projects.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1300px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {projects.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No projects found.
                      </td>
                    </tr>
                  ) : (
                    projects.map((project, index) => {
                      const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                      return (
                        <tr
                          key={project.id}
                          className={`${rowBg} hover:bg-blue-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/project/${project.id}`}
                              className="text-blue-600 hover:text-blue-800 underline font-medium"
                            >
                              {`PRJ-${String(project.id).padStart(3, "0")}`}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {project.project_name}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {project.client?.id ? (
                              <Link
                                to={`/clients/${project.client.id}`}
                                className="text-blue-600 hover:text-blue-800 underline font-medium"
                              >
                                {getClientName(project.client)}
                              </Link>
                            ) : (
                              getClientName(project.client)
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {project.start_at ? formatDate(project.start_at) : "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {project.deadline_at ? formatDate(project.deadline_at) : "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700 capitalize">
                            {project.status || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {project.primaryManager
                              ? `${project.primaryManager.first_name} ${project.primaryManager.last_name}`
                              : "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-right text-gray-700">—</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-right text-gray-700">—</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-right text-gray-700">—</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectsPage;
